import { useEffect, useMemo, useState, type ReactNode } from "react";
import type {
  GrupoQualificadoDto,
  ResultadoTesteGrupoDto,
} from "@base/contracts";
import {
  Botao,
  CampoDeTexto,
  EstadoCarregando,
  EstadoDeErro,
  EstadoVazio,
  cn,
} from "@base/ui";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Check,
  ChevronRight,
  Link2,
  ListChecks,
  MessagesSquare,
  Pencil,
  Play,
  Save,
  Search,
  Smartphone,
} from "lucide-react";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { avisar } from "../../../shared/ui/dialogos-api";
import {
  useGruposQualificados,
  useInstanciasWaha,
  useSalvarInstanciasWaha,
  useSalvarListIdGrupos,
  useSalvarWaha,
  useStatusMonitoramentoGrupos,
  useTestarGrupoMonitoramento,
} from "../application/use-monitoramento-grupos";

type EditorAberto = "waha" | "clickup" | "instancias" | null;

/**
 * Configuração WAHA + ClickUp + teste, com setup compacto e edição em gaveta.
 */
export function PainelMonitoramentoDeGrupos() {
  const status = useStatusMonitoramentoGrupos();
  const [editor, definirEditor] = useState<EditorAberto>(null);

  if (status.isPending) {
    return <EstadoCarregando rotulo="Carregando monitoramento…" />;
  }

  if (status.isError) {
    return (
      <EstadoDeErro
        titulo="Não foi possível carregar o monitoramento"
        mensagem={
          status.error instanceof ErroDaApi && status.error.status === 403
            ? "Você precisa de permissão para este recurso."
            : status.error instanceof Error
              ? status.error.message
              : "Erro inesperado."
        }
        aoTentarNovamente={() => void status.refetch()}
      />
    );
  }

  const dados = status.data!;
  const wahaOk = dados.wahaConfigurado;
  const clickupOk =
    Boolean(dados.clickupListIdGrupos?.trim()) && dados.clickupTokenOk;
  const temInstancias = dados.instanciasSelecionadas.length > 0;
  const pronto = dados.prontoParaUsar;

  const passoAtivo: EditorAberto = !wahaOk
    ? "waha"
    : !clickupOk
      ? "clickup"
      : !temInstancias
        ? "instancias"
        : null;

  return (
    <div className="dashboard-atmosfera mx-auto flex w-full max-w-4xl flex-col gap-8 pb-8">
      <CabecalhoHero
        pronto={pronto}
        instancias={dados.instanciasSelecionadas.length}
      />

      <section className="space-y-3" aria-label="Configuração">
        <div className="flex items-end justify-between gap-3">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--rk-text-brand)]">
              Setup
            </p>
            <h3 className="text-sm font-semibold tracking-tight text-foreground">
              Conexões
            </h3>
          </div>
          <p className="text-xs text-muted-foreground">
            {[wahaOk, clickupOk, temInstancias].filter(Boolean).length}/3 prontas
          </p>
        </div>

        <div className="grid gap-3">
          <CartaoResumo
            icone={<Link2 className="size-4" aria-hidden="true" />}
            titulo="Conexão WAHA"
            resumo={
              wahaOk
                ? encurtarUrl(dados.wahaBaseUrl)
                : "URL e token da API ainda não salvos"
            }
            pronto={wahaOk}
            ativo={passoAtivo === "waha"}
            aoEditar={() => definirEditor("waha")}
            rotuloAcao={wahaOk ? "Editar" : "Configurar"}
          />
          <CartaoResumo
            icone={<ListChecks className="size-4" aria-hidden="true" />}
            titulo="Lista do ClickUp"
            resumo={
              !dados.clickupTokenOk
                ? "Token ClickUp ausente na conta"
                : clickupOk
                  ? `Lista ${dados.clickupListIdGrupos}`
                  : "Informe o ID da lista de grupos"
            }
            pronto={clickupOk}
            ativo={passoAtivo === "clickup"}
            bloqueado={!wahaOk}
            aoEditar={() => definirEditor("clickup")}
            rotuloAcao={clickupOk ? "Editar" : "Configurar"}
          />
          <CartaoResumo
            icone={<Smartphone className="size-4" aria-hidden="true" />}
            titulo="Instâncias"
            resumo={
              temInstancias
                ? `${dados.instanciasSelecionadas.length} sessão(ões): ${resumoInstancias(dados.instanciasSelecionadas)}`
                : "Escolha quais números entram no monitoramento"
            }
            pronto={temInstancias}
            ativo={passoAtivo === "instancias"}
            bloqueado={!wahaOk}
            aoEditar={() => definirEditor("instancias")}
            rotuloAcao={temInstancias ? "Editar" : "Selecionar"}
          />
        </div>

        {passoAtivo && editor === null ? (
          <PainelFormulario
            titulo={tituloDoPasso(passoAtivo)}
            descricao={descricaoDoPasso(passoAtivo)}
          >
            {passoAtivo === "waha" ? (
              <FormWaha
                urlAtual={dados.wahaBaseUrl ?? ""}
                onSalvo={() => definirEditor(null)}
              />
            ) : null}
            {passoAtivo === "clickup" ? (
              <FormClickUp
                listIdAtual={dados.clickupListIdGrupos ?? ""}
                tokenOk={dados.clickupTokenOk}
                onSalvo={() => definirEditor(null)}
              />
            ) : null}
            {passoAtivo === "instancias" ? (
              <FormInstancias onSalvo={() => definirEditor(null)} />
            ) : null}
          </PainelFormulario>
        ) : null}
      </section>

      {temInstancias ? (
        <SecaoTeste clickupPronto={pronto} />
      ) : (
        <EstadoVazio
          icone={<MessagesSquare className="size-8" aria-hidden="true" />}
          titulo="Falta só escolher as instâncias"
          descricao="Quando as sessões estiverem marcadas, você testa um grupo e o cron diário passa a rodar sozinho."
        />
      )}

      <Sheet
        open={editor !== null}
        onOpenChange={(aberto) => {
          if (!aberto) definirEditor(null);
        }}
      >
        <SheetContent side="right" className="sm:max-w-lg">
          {editor ? (
            <>
              <SheetHeader>
                <SheetTitle>{tituloDoPasso(editor)}</SheetTitle>
                <SheetDescription>{descricaoDoPasso(editor)}</SheetDescription>
              </SheetHeader>
              <SheetBody className="space-y-6">
                {editor === "waha" ? (
                  <FormWaha
                    urlAtual={dados.wahaBaseUrl ?? ""}
                    onSalvo={() => definirEditor(null)}
                    emSheet
                  />
                ) : null}
                {editor === "clickup" ? (
                  <FormClickUp
                    listIdAtual={dados.clickupListIdGrupos ?? ""}
                    tokenOk={dados.clickupTokenOk}
                    onSalvo={() => definirEditor(null)}
                    emSheet
                  />
                ) : null}
                {editor === "instancias" ? (
                  <FormInstancias
                    onSalvo={() => definirEditor(null)}
                    emSheet
                  />
                ) : null}
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CabecalhoHero({
  pronto,
  instancias,
}: {
  readonly pronto: boolean;
  readonly instancias: number;
}) {
  return (
    <header className="dashboard-hero space-y-4 border-b border-border/60 pb-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--rk-text-brand)]">
        Operação
      </p>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl space-y-3">
          <h2 className="font-[family-name:var(--rk-font-display,inherit)] text-3xl font-semibold leading-[1.08] tracking-[-0.04em] text-foreground sm:text-4xl">
            Monitoramento de Grupos
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            Resumo diário dos grupos “Conteúdos - …” vira comentário na task do
            cliente no ClickUp. Configure uma vez; o resto roda à meia-noite.
          </p>
        </div>
        <Badge variant={pronto ? "secondary" : "outline"} className="shrink-0">
          {pronto
            ? `Pronto · ${instancias} instância${instancias === 1 ? "" : "s"}`
            : "Em configuração"}
        </Badge>
      </div>
    </header>
  );
}

function CartaoResumo({
  icone,
  titulo,
  resumo,
  pronto,
  ativo,
  bloqueado = false,
  aoEditar,
  rotuloAcao,
}: {
  readonly icone: ReactNode;
  readonly titulo: string;
  readonly resumo: string;
  readonly pronto: boolean;
  readonly ativo: boolean;
  readonly bloqueado?: boolean;
  readonly aoEditar: () => void;
  readonly rotuloAcao: string;
}) {
  return (
    <div
      className={cn(
        "dashboard-painel flex items-center gap-4 rounded-[var(--rk-radius-lg,1rem)] border px-4 py-4 transition-[border-color,box-shadow] duration-200 sm:px-5",
        ativo && !pronto
          ? "border-[color:var(--rk-border-brand)] bg-[color:var(--rk-bg-brand-soft)]/40"
          : "border-border/80 bg-card",
        bloqueado && "opacity-55",
      )}
    >
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-xl border",
          pronto
            ? "border-ink bg-ink text-ink-foreground"
            : "border-border/80 bg-muted/40 text-muted-foreground",
        )}
        aria-hidden="true"
      >
        {pronto ? <Check className="size-4" /> : icone}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold tracking-tight text-foreground">
            {titulo}
          </p>
          <Badge variant={pronto ? "secondary" : "outline"}>
            {pronto ? "Ok" : ativo ? "Agora" : "Pendente"}
          </Badge>
        </div>
        <p className="truncate text-sm text-muted-foreground">{resumo}</p>
      </div>
      <Botao
        variante="contorno"
        tamanho="pequeno"
        className="cursor-pointer shrink-0"
        disabled={bloqueado}
        onClick={aoEditar}
      >
        {pronto ? <Pencil className="size-3.5" aria-hidden="true" /> : null}
        {rotuloAcao}
        <ChevronRight className="size-3.5 opacity-60" aria-hidden="true" />
      </Botao>
    </div>
  );
}

function PainelFormulario({
  titulo,
  descricao,
  children,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="dashboard-painel space-y-5 rounded-[var(--rk-radius-lg,1rem)] border border-border/80 bg-card p-5 sm:p-6">
      <div className="space-y-2">
        <h4 className="text-base font-semibold tracking-tight">{titulo}</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {descricao}
        </p>
      </div>
      {children}
    </section>
  );
}

function FormWaha({
  urlAtual,
  onSalvo,
  emSheet = false,
}: {
  readonly urlAtual: string;
  readonly onSalvo: () => void;
  readonly emSheet?: boolean;
}) {
  const salvar = useSalvarWaha();
  const [url, definirUrl] = useState(urlAtual);
  const [token, definirToken] = useState("");

  useEffect(() => {
    definirUrl(urlAtual);
  }, [urlAtual]);

  const acoes = (
    <Botao
      className="cursor-pointer"
      disabled={salvar.isPending || !url.trim() || !token.trim()}
      onClick={() => {
        void salvar
          .mutateAsync({ wahaBaseUrl: url.trim(), wahaToken: token.trim() })
          .then(() => {
            definirToken("");
            avisar({
              titulo: "WAHA salvo",
              descricao: "Próximo passo: lista do ClickUp e instâncias.",
            });
            onSalvo();
          })
          .catch((erro: unknown) =>
            avisar({
              titulo: "Não foi possível salvar o WAHA",
              descricao:
                erro instanceof Error ? erro.message : "Tente de novo.",
            }),
          );
      }}
    >
      <Save className="size-4" aria-hidden="true" />
      Salvar conexão
    </Botao>
  );

  return (
    <div className="space-y-4">
      <CampoDeTexto
        rotulo="URL da API"
        value={url}
        onChange={(e) => definirUrl(e.target.value)}
        placeholder="https://waha.seudominio.com"
      />
      <CampoDeTexto
        rotulo="Token (X-Api-Key)"
        value={token}
        onChange={(e) => definirToken(e.target.value)}
        type="password"
        placeholder={urlAtual ? "•••••••• (cole para trocar)" : "seu token"}
      />
      {emSheet ? (
        <div className="mt-2 flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:justify-end">
          {acoes}
        </div>
      ) : (
        acoes
      )}
    </div>
  );
}

function FormClickUp({
  listIdAtual,
  tokenOk,
  onSalvo,
  emSheet = false,
}: {
  readonly listIdAtual: string;
  readonly tokenOk: boolean;
  readonly onSalvo: () => void;
  readonly emSheet?: boolean;
}) {
  const salvar = useSalvarListIdGrupos();
  const [listId, definirListId] = useState(listIdAtual);

  useEffect(() => {
    definirListId(listIdAtual);
  }, [listIdAtual]);

  const acoes = (
    <Botao
      className="cursor-pointer"
      disabled={salvar.isPending || !listId.trim() || !tokenOk}
      onClick={() => {
        void salvar
          .mutateAsync({ clickupListIdGrupos: listId.trim() })
          .then(() => {
            avisar({
              titulo: "Lista salva",
              descricao: "Tasks de monitoramento vão para esta lista.",
            });
            onSalvo();
          })
          .catch((erro: unknown) =>
            avisar({
              titulo: "Não foi possível salvar a lista",
              descricao:
                erro instanceof Error ? erro.message : "Tente de novo.",
            }),
          );
      }}
    >
      <Save className="size-4" aria-hidden="true" />
      Salvar lista
    </Botao>
  );

  return (
    <div className="space-y-4">
      {!tokenOk ? (
        <p className="text-sm text-destructive">
          Token ClickUp ausente. Configure em Configurações da conta → ClickUp.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Usamos o token já salvo na conta. Aqui você só aponta a lista das
          tasks dos clientes.
        </p>
      )}
      <CampoDeTexto
        rotulo="ID da lista"
        value={listId}
        onChange={(e) => definirListId(e.target.value)}
        placeholder="9011…"
      />
      {emSheet ? (
        <div className="mt-2 flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:justify-end">
          {acoes}
        </div>
      ) : (
        acoes
      )}
    </div>
  );
}

function FormInstancias({
  onSalvo,
  emSheet = false,
}: {
  readonly onSalvo: () => void;
  readonly emSheet?: boolean;
}) {
  const instancias = useInstanciasWaha(true);
  const salvar = useSalvarInstanciasWaha();
  const [marcadas, definirMarcadas] = useState<string[]>([]);
  const [busca, definirBusca] = useState("");

  useEffect(() => {
    if (!instancias.data) return;
    definirMarcadas(
      instancias.data.filter((i) => i.selecionada).map((i) => i.nome),
    );
  }, [instancias.data]);

  const filtradas = useMemo(() => {
    const lista = instancias.data ?? [];
    const termo = busca.trim().toLocaleLowerCase("pt-BR");
    if (!termo) return lista;
    return lista.filter(
      (item) =>
        item.nome.toLocaleLowerCase("pt-BR").includes(termo) ||
        item.status.toLocaleLowerCase("pt-BR").includes(termo),
    );
  }, [instancias.data, busca]);

  const nomesFiltrados = useMemo(
    () => filtradas.map((item) => item.nome),
    [filtradas],
  );
  const todasFiltradasMarcadas =
    nomesFiltrados.length > 0 &&
    nomesFiltrados.every((nome) => marcadas.includes(nome));

  const acoes = (
    <div className="flex flex-wrap gap-3">
      <Botao
        variante="contorno"
        className="cursor-pointer"
        disabled={instancias.isFetching}
        onClick={() => void instancias.refetch()}
      >
        Atualizar lista
      </Botao>
      <Botao
        className="cursor-pointer"
        disabled={salvar.isPending || marcadas.length === 0}
        onClick={() => {
          void salvar
            .mutateAsync({ instancias: marcadas })
            .then(() => {
              avisar({
                titulo: "Instâncias salvas",
                descricao: "Só as marcadas entram no monitoramento.",
              });
              onSalvo();
            })
            .catch((erro: unknown) =>
              avisar({
                titulo: "Não foi possível salvar",
                descricao:
                  erro instanceof Error ? erro.message : "Tente de novo.",
              }),
            );
        }}
      >
        <Save className="size-4" aria-hidden="true" />
        Salvar ({marcadas.length})
      </Botao>
    </div>
  );

  return (
    <div className="space-y-4">
      {instancias.isPending ? (
        <EstadoCarregando rotulo="Buscando sessões no WAHA…" />
      ) : null}
      {instancias.isError ? (
        <p className="text-sm text-destructive">
          {instancias.error instanceof Error
            ? instancias.error.message
            : "Falha ao listar instâncias."}
        </p>
      ) : null}
      {instancias.data && instancias.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma sessão encontrada no WAHA.
        </p>
      ) : null}

      {instancias.data && instancias.data.length > 0 ? (
        <>
          <CampoDeTexto
            rotulo="Buscar instância"
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="Nome ou status"
            icone={<Search className="size-4" aria-hidden="true" />}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {marcadas.length} selecionada{marcadas.length === 1 ? "" : "s"}
              {busca.trim()
                ? ` · ${filtradas.length} na busca`
                : ` · ${instancias.data.length} no total`}
            </p>
            <Botao
              variante="fantasma"
              className="cursor-pointer"
              disabled={nomesFiltrados.length === 0}
              onClick={() => {
                if (todasFiltradasMarcadas) {
                  definirMarcadas((atual) =>
                    atual.filter((nome) => !nomesFiltrados.includes(nome)),
                  );
                  return;
                }
                definirMarcadas((atual) => [
                  ...new Set([...atual, ...nomesFiltrados]),
                ]);
              }}
            >
              {todasFiltradasMarcadas
                ? "Desmarcar filtradas"
                : "Selecionar filtradas"}
            </Botao>
          </div>

          {filtradas.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma instância com “{busca.trim()}”.
            </p>
          ) : (
            <ul className="flex max-h-[min(50vh,22rem)] flex-col gap-3 overflow-y-auto pr-1">
              {filtradas.map((item) => {
                const ativo = marcadas.includes(item.nome);
                return (
                  <li key={item.nome}>
                    <button
                      type="button"
                      aria-pressed={ativo}
                      className={cn(
                        "flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors duration-200",
                        ativo
                          ? "border-border bg-accent"
                          : "border-border/70 hover:bg-muted/50",
                      )}
                      onClick={() =>
                        definirMarcadas((atual) =>
                          ativo
                            ? atual.filter((n) => n !== item.nome)
                            : [...atual, item.nome],
                        )
                      }
                    >
                      <span className="flex min-w-0 items-start gap-3">
                        <span
                          className={cn(
                            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border",
                            ativo
                              ? "border-ink bg-ink text-ink-foreground"
                              : "border-border bg-background",
                          )}
                          aria-hidden="true"
                        >
                          {ativo ? <Check className="size-3.5" /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">
                            {item.nome}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Status: {item.status}
                          </span>
                        </span>
                      </span>
                      <Badge variant={ativo ? "secondary" : "outline"}>
                        {ativo ? "Selecionada" : "Fora"}
                      </Badge>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}

      {emSheet ? (
        <div className="mt-2 flex flex-col gap-3 border-t border-border/60 pt-4">
          {acoes}
        </div>
      ) : (
        acoes
      )}
    </div>
  );
}

function SecaoTeste({ clickupPronto }: { readonly clickupPronto: boolean }) {
  const grupos = useGruposQualificados(true);
  const testar = useTestarGrupoMonitoramento();
  const [selecionado, definirSelecionado] = useState<GrupoQualificadoDto | null>(
    null,
  );
  const [resultado, definirResultado] = useState<ResultadoTesteGrupoDto | null>(
    null,
  );
  const [buscaGrupo, definirBuscaGrupo] = useState("");

  const gruposFiltrados = useMemo(() => {
    const lista = grupos.data ?? [];
    const termo = buscaGrupo.trim().toLocaleLowerCase("pt-BR");
    if (!termo) return lista;
    return lista.filter(
      (g) =>
        g.nomeCliente.toLocaleLowerCase("pt-BR").includes(termo) ||
        g.nome.toLocaleLowerCase("pt-BR").includes(termo) ||
        g.sessao.toLocaleLowerCase("pt-BR").includes(termo),
    );
  }, [grupos.data, buscaGrupo]);

  return (
    <section className="dashboard-painel overflow-hidden rounded-[var(--rk-radius-lg,1rem)] border border-border/80 bg-card shadow-[var(--rk-shadow-sm)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 px-5 py-5 sm:px-6">
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--rk-text-brand)]">
            Teste
          </p>
          <h3 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
            Rodar um grupo agora
          </h3>
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
            Pega o dia anterior (Brasília), resume com IA e comenta na task do
            cliente.
          </p>
          {!clickupPronto ? (
            <p className="text-sm text-destructive">
              Para rodar o teste, finalize a lista do ClickUp e o token da conta.
            </p>
          ) : null}
        </div>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
        {grupos.isPending ? (
          <EstadoCarregando rotulo="Listando grupos qualificados…" />
        ) : null}

        {grupos.isError ? (
          <p className="text-sm text-destructive">
            {grupos.error instanceof Error
              ? grupos.error.message
              : "Falha ao listar grupos."}
          </p>
        ) : null}

        {grupos.data && grupos.data.length === 0 ? (
          <EstadoVazio
            icone={<MessagesSquare className="size-8" aria-hidden="true" />}
            titulo="Nenhum grupo no padrão"
            descricao='Procuramos nomes como "Conteúdos - Nome do cliente" nas instâncias selecionadas.'
          />
        ) : null}

        {grupos.data && grupos.data.length > 0 ? (
          <div className="flex flex-col gap-4">
            <CampoDeTexto
              rotulo="Buscar grupo"
              value={buscaGrupo}
              onChange={(e) => definirBuscaGrupo(e.target.value)}
              placeholder="Cliente, grupo ou sessão"
              icone={<Search className="size-4" aria-hidden="true" />}
            />
            <p className="text-sm text-muted-foreground">
              {gruposFiltrados.length} de {grupos.data.length} grupo
              {grupos.data.length === 1 ? "" : "s"}
            </p>
            {gruposFiltrados.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum grupo com “{buscaGrupo.trim()}”.
              </p>
            ) : (
              <ul className="flex max-h-80 flex-col gap-3 overflow-y-auto pr-1">
                {gruposFiltrados.map((grupo) => {
                  const ativo =
                    selecionado?.chatId === grupo.chatId &&
                    selecionado.sessao === grupo.sessao;
                  return (
                    <li key={`${grupo.sessao}:${grupo.chatId}`}>
                      <button
                        type="button"
                        className={cn(
                          "flex w-full cursor-pointer flex-col gap-1 rounded-lg border px-4 py-3 text-left transition-colors duration-200",
                          ativo
                            ? "border-border bg-accent"
                            : "border-border/70 hover:bg-muted/50",
                        )}
                        onClick={() => definirSelecionado(grupo)}
                      >
                        <span className="text-sm font-medium">
                          {grupo.nomeCliente}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {grupo.nome} · sessão {grupo.sessao}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}

        <Botao
          className="cursor-pointer"
          disabled={!selecionado || testar.isPending || !clickupPronto}
          onClick={() => {
            if (!selecionado) return;
            definirResultado(null);
            void testar
              .mutateAsync({
                sessao: selecionado.sessao,
                chatId: selecionado.chatId,
                nomeGrupo: selecionado.nome,
              })
              .then((saida) => {
                definirResultado(saida);
                avisar({
                  titulo: saida.taskNova ? "Task criada" : "Comentário enviado",
                  descricao: `${saida.nomeCliente}: ${saida.mensagens} mensagem(ns) em ${saida.dataReferencia}.`,
                });
              })
              .catch((erro: unknown) =>
                avisar({
                  titulo: "Teste falhou",
                  descricao:
                    erro instanceof Error ? erro.message : "Tente de novo.",
                }),
              );
          }}
        >
          <Play className="size-4" aria-hidden="true" />
          {testar.isPending ? "Processando…" : "Rodar teste neste grupo"}
        </Botao>

        {resultado ? (
          <div className="space-y-3 rounded-xl border border-border/70 bg-muted/30 p-5">
            <p className="text-sm font-medium">
              {resultado.nomeCliente} · {resultado.dataReferencia}
            </p>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {resultado.resumo}
            </p>
            {resultado.clickupTaskUrl ? (
              <a
                href={resultado.clickupTaskUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex cursor-pointer text-sm font-medium text-[color:var(--rk-text-brand)] underline-offset-4 hover:underline"
              >
                Abrir task no ClickUp
              </a>
            ) : (
              <p className="text-xs text-muted-foreground">
                Task ID: {resultado.clickupTaskId}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function tituloDoPasso(passo: Exclude<EditorAberto, null>): string {
  if (passo === "waha") return "Conexão WAHA";
  if (passo === "clickup") return "Lista do ClickUp";
  return "Instâncias WAHA";
}

function descricaoDoPasso(passo: Exclude<EditorAberto, null>): string {
  if (passo === "waha") {
    return "URL e token da API. Depois disso liberamos lista e sessões.";
  }
  if (passo === "clickup") {
    return "Aponta a lista onde nascem as tasks dos clientes.";
  }
  return "Marque só as sessões que devem entrar no monitoramento.";
}

function encurtarUrl(url: string | null): string {
  if (!url) return "Configurado";
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function resumoInstancias(nomes: readonly string[]): string {
  if (nomes.length <= 2) return nomes.join(", ");
  return `${nomes.slice(0, 2).join(", ")} +${nomes.length - 2}`;
}
